import { LiveStatusEnum } from 'features/lives';
import { TimelineItemState } from 'features/timeline';
import { TeachersCardState } from 'features/teachers';
import { VideoChatProviderEnum } from 'features/chat';
import { ProductTypeEnum } from 'features/products';

export enum ContentTypeEnum {
  /// <summary>
  /// Not specified
  /// </summary>
  Unspecified = 0,
  /// <summary>
  /// Video (live or recorded)
  /// </summary>
  Video = 1,
  /// <summary>
  /// Article (text)
  /// </summary>
  Article = 2,
  /// <summary>
  /// Quiz (questions with multiple answers to choice)
  /// </summary>
  Quiz = 3,
  /// <summary>
  /// Scorm
  /// </summary>
  Scorm = 4,
  /// <summary>
  /// Survey
  /// </summary>
  Survey = 5,
  /// <summary>
  /// ExternalDocument
  /// </summary>
  ExternalDocument = 6,
}

export enum VideoProviderEnum {
  Unspecified = 0,
  Vimeo = 1,
  ProtectedVideo = 2,
  Youtube = 3,
  Panda = 4,
  Zoom = 5,
  SambaVideo = 6,
}

export enum ReleaseTypes {
  // Not specified (always open)
  Unspecified = 0,

  // After subscribe
  Subscribe = 1,

  // Some days after subscribe
  DaysAfterSubscribe = 2,

  // When parent content/product open
  Parent = 3,

  // At specific date
  SpecificDate = 4,

  // After completing content/product
  AfterCompleting = 5,
}

export enum ClosingTypes {
  /// <summary>
  /// Not specified (not closing)
  /// </summary>
  Unspecified = 0,
  /// <summary>
  /// Days after content released
  /// </summary>
  DaysAfterRelease = 1,
  /// <summary>
  /// Specific date
  /// </summary>
  SpecificDate = 2,
  /// <summary>
  /// Days after enrollment
  /// </summary>
  DaysAfterEnrollment = 3,
}

export enum ContentExternalDocumentTypeEnum {
  PDF = 1,
  Audio = 2,
}

export interface ContentsItem {
  uuid: string;
  completionPercentage: number;
  contentType: ContentTypeEnum;
  description: string;
  durationSeconds: number;
  isComplete: boolean;
  isLiveMeeting: boolean;
  liveStatus: LiveStatusEnum;
  name: string;
  videoCurrentPointInTime: number;
  videoCompletedPointInTime: number;
  numberOfQuestions: number | null;
  timelineOrder: number;
  isOptional: boolean;
  showDate: boolean;
  releaseDate: string | null;

  isBlocked: boolean;
  productClassUuid: string;
  released: boolean;
  createdAt?: string;
  updatedAt?: string;
  releaseDays?: number;
  completedAt?: string;
  externalDocumentType?: ContentExternalDocumentTypeEnum;
}

export interface ContentVideoPlayer {
  videoProvider: VideoProviderEnum;
  externalVideoProviderIdOrUrl: string;

  protectedVideoOtp: string;
  protectedVideoPlaybackInfo: string;
  protectedVideoPlayerTheme: string;
}

interface ContentVideoState {
  isLiveMeeting: boolean;
  liveStatus: LiveStatusEnum;
  videoProvider: VideoProviderEnum;
  externalVideoProviderIdOrUrl: string;
  externalLiveMeetingInvitationUrl: string;
  videoChatProvider: VideoChatProviderEnum;
  externalVideoChatProviderIdOrUrl: string;
  durationSeconds: number;
  timeline: TimelineItemState[];

  protectedVideoOtp: string;
  protectedVideoPlaybackInfo: string;
  protectedVideoPlayerTheme: string;

  checkpoints: number[];

  transcription?: string;
}

export interface ContentQuizAnswerState {
  answerUuid: string;
  answerText: string;
}

export interface ContentQuizQuestionApiResponse {
  results: [
    {
      questionUuid: string;
      questionAnswerChoiceUuid: string;
      commentedAnswer?: string;
      rightQuestionAnswerChoiceUuid?: string;
      videoProvider?: VideoProviderEnum;
      externalVideoProviderIdOrUrl?: string;

      protectedVideoOtp: string;
      protectedVideoPlaybackInfo: string;
      protectedVideoPlayerTheme: string;
    }
  ];
}

export interface SendFeedbackProps {
  liked: boolean;
  feedbackText?: string;
}
export interface ContentQuizQuestionState {
  questionUuid: string;
  questionText: string;
  answerChoices: ContentQuizAnswerState[];
  // TODO: Isso abaixo é usado para gerar um objeto para facilitar o update e envio pro back
  answerUuid?: string;
  currentQuestion?: boolean;
  questionIndex?: number;
  isFirst?: boolean;
  isLast?: boolean;
  nextQuestionUuid?: string;
  prevQuestionUuid?: string;
  commentedAnswer?: string;
  rightQuestionAnswerChoiceUuid?: string;
  videoProvider?: VideoProviderEnum;
  externalVideoProviderIdOrUrl?: string;
  protectedVideoOtp: string;
  protectedVideoPlaybackInfo: string;
  protectedVideoPlayerTheme: string;
}

export type ContentQuizQuestionsState = Record<string, ContentQuizQuestionState>;

export interface ContentQuizState {
  questions: ContentQuizQuestionState[];
}

export interface ContentArticleState {
  articleText: string;
}

export interface PrevNextLiveState {
  uuid: 'b343198b-c109-425e-9f46-4055e16b5af2';
  slug: 'na-rota-do-mercado-financeiro';
  date: '2021-10-03T23:00:00+00:00';
  name: 'Atitudes';
  description: null;
  guests: '';
  teacher: {
    teacherUuid: '055c762a-a988-41b1-a83a-65964a59186f';
    name: 'Raony Rossetti';
    slug: 'raony-rossetti';
    cardImageUrl: '/images/raony-card.png';
    coverImageUrl: '/images/raony-pagina.png';
    initialDescription: 'CEO e fundador da MELVER. Formado em Engenharia Mecânica pela Escola Politécnica da Universidade de São Paulo e MBA em Gestão da Tecnologia da Informação pela FIAP.';
    phrase: 'Sobre o professor';
    additionalDescription: 'Iniciou sua carreira no mercado financeiro em 2008, como estagiário da mesa institucional da XP. Criou, em 2009, a área que se tornou o segmento de Produtos Estruturados. Atuou como broker de 2009 a 2014. Em 2014, tornou-se <i>head</i> de Renda Variável da XP. Alguns anos depois, assumiu a Clear e também a Renda Variável na Rico. Em 2018, mudou para Nova Iorque, ocupando o papel de CIO da XP Inc. Liderou equipes no Brasil, nos Estados Unidos e na Índia. Fundou a MELVER em 2020.<br />\nÉ casado com a Jaqueline, pai do Pedro e da Manuela.';
    professionalDescription: 'A trajetória do professor';
    tags: ['MERCADO À VISTA', 'MERCADO FUTURO', 'DERIVATIVOS', 'RISCO', 'ALTA PERFORMANCE', 'PRODUTIVIDADE', 'EMPREENDEDORISMO', 'LIDERANÇA'];
  };
  imageUrl: '';
  liveStatus: 4;
  videoProvider: 1;
  externalVideoProviderIdOrUrl: '623841222';
  externalLiveMeetingInvitationUrl: 'https://us02web.zoom.us/j/84509708566';
  durationSeconds: 6701;
}

export interface ProductContentItemExternalDocumentResponse {
  externalDocumentType: ContentExternalDocumentTypeEnum;
}

export interface ContentDetailsState {
  productClassUuid: string;
  video?: ContentVideoState;
  article?: ContentArticleState;
  quiz?: ContentQuizState;
  assessment: {
    videoCurrentPointInTime: number;
    videoCompletedPointInTime: number;
    completionPercentage: number;
    isComplete: boolean;
    liked: boolean;
    feedbackText: string;
    quizes: [];
    notes?: string;
  };
  teacher: TeachersCardState | null;
  downloads: [];
  contentUuid: string;
  contentExternalId: null;
  name: string;
  description: string;
  imageUrl: string;
  contentType: ContentTypeEnum;
  contentPartner: null;

  productClassTimelineUuid: string;
  productClassTimelineName: string;

  previousContentType: ContentTypeEnum;
  previousExternalDocumentType?: ContentExternalDocumentTypeEnum;
  previousContentUuid: string;
  previousContentName: string;
  previousProductClassTimelineUuid: string;
  previousProductClassTimelineName: string;

  nextContentType: ContentTypeEnum;
  nextExternalDocumentType?: ContentExternalDocumentTypeEnum;
  nextContentUuid: string;
  nextContentName: string;
  nextProductClassTimelineUuid: string;
  nextProductClassTimelineName: string;

  timelineCount: number;
  timelineOrder: number;

  // TODO: Verificar prev and next product organization
  productOrganizationUuid: null;
  previousProductOrganizationUuid: null;
  nextProductOrganizationUuid: null;
  productUuid: string;
  productName: string;
  productClassDescription: string;
  releaseDate: string;
  guests: string;
  previousAndNextLives: PrevNextLiveState[];

  productType: ProductTypeEnum;

  survey?: {
    identification: string;
    name: string;
    description: string;
    formData: string;
  };

  showDate: boolean;
  externalDocument: ProductContentItemExternalDocumentResponse;
}

// export interface ContentDetailsApiResponse {}

export interface ContentDetailsApiResponse extends ContentDetailsState {}

export interface ContentsState {
  contentDetails: ContentDetailsState;
  contentsList: ContentsItem[];
}

export interface UseContentsState {
  contentDetails: ContentDetailsState;
  setContentDetails: (contentDetails: ContentDetailsApiResponse) => void;
  clearContentDetails: () => void;

  contentsList: ContentsItem[];
  setContentsList: (contentsList: ContentsItem[]) => void;
  clearContentsList: () => void;
}
